import {
  Box,
  Button,
  Card,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import Select from '@mui/material/Select';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import moment from 'moment';
import React from 'react';
import { Control, Controller, UseFormWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EHolidayType } from '../../../../shared/enums/holiday/EHoliday';
import { EReligion } from '../../../../shared/enums/user/EReligion';
import { IHoliday } from '../../../../shared/interfaces/holiday/IHoliday';
import { RootState } from '../../../../store/store';
import i18n from '../../../../utils/localization/i18n';

interface IProps {
  isUpdate?: boolean;
  watch: UseFormWatch<IHoliday>;
  control: Control<IHoliday>;
}
const HolidayForm = ({ control, isUpdate = false, watch }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { country: countries } = useSelector((state: RootState) => state.enums);
  return (
    <Card>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Controller
            name={'name'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                error={!!error?.message}
                helperText={error?.message ? t(error?.message) : ''}
                value={value ?? ''}
                onChange={onChange}
                label={t('general.name')}
                sx={{ width: '100%' }}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Controller
            name={'country'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? 'Mui-error' : ''}
                  id="country-label"
                >
                  {t('general.country')}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="country-label"
                  id="country-select"
                  value={value ?? ''}
                  label={t('general.country')}
                  onChange={onChange}
                >
                  {countries?.map((country) => {
                    return (
                      <MenuItem key={country} value={country}>
                        {t(`country.${country}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>

        <Grid size={{ xs: 6, lg: 4 }}>
          <Controller
            name={'holidayType'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? 'Mui-error' : ''}
                  id="status-label"
                >
                  {t('holidays.holidayType')}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="status-label"
                  id="status-select"
                  value={value ?? ''}
                  label={t('holidays.holidayType')}
                  onChange={onChange}
                >
                  {Object.values(EHolidayType)?.map((holidayType) => {
                    return (
                      <MenuItem key={holidayType} value={holidayType}>
                        {t(`holidays.type.${holidayType}`)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        {watch('holidayType') === EHolidayType.RELIGIOUS && (
          <Grid size={{ xs: 6, lg: 2 }}>
            <Controller
              name={'religion'}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <FormControl fullWidth>
                  <InputLabel
                    className={!!error?.message?.length ? 'Mui-error' : ''}
                    id="religion-label"
                  >
                    {t('general.religion')}
                  </InputLabel>
                  <Select
                    error={!!error?.message}
                    labelId="religion-label"
                    id="religion-select"
                    value={value ?? ''}
                    label={t('general.religion')}
                    onChange={onChange}
                  >
                    {Object.values(EReligion)?.map((religion) => {
                      return (
                        <MenuItem key={religion} value={religion}>
                          {t(`religion.${religion}`)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {!!error?.message?.length && (
                    <FormHelperText>{t(error?.message)}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Grid>
        )}
        <Grid
          size={
            watch('holidayType') === EHolidayType.RELIGIOUS
              ? { xs: 6, lg: 2 }
              : { xs: 6, lg: 4 }
          }
        >
          <Controller
            name={'year'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  className={!!error?.message?.length ? 'Mui-error' : ''}
                  id="year-label"
                >
                  {t('general.year')}
                </InputLabel>
                <Select
                  error={!!error?.message}
                  labelId="year-label"
                  id="year-select"
                  value={value ?? ''}
                  label={t('general.year')}
                  onChange={onChange}
                >
                  {[2025, 2026, 2027, 2028]?.map((year) => {
                    return (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Controller
            name={'startDate'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={i18n.language}
                >
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      label={t('general.startDate')}
                      value={moment(value) ?? ''}
                      onChange={onChange}
                      format="DD.MM.YYYY"
                      slotProps={{
                        textField: {
                          error: !!error,
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Controller
            name={'endDate'}
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <>
                <LocalizationProvider
                  dateAdapter={AdapterMoment}
                  adapterLocale={i18n.language}
                >
                  <DemoContainer components={['DatePicker']}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      label={t('general.endDate')}
                      value={moment(value) ?? ''}
                      format="DD.MM.YYYY"
                      onChange={onChange}
                      slotProps={{
                        textField: {
                          error: !!error,
                        },
                      }}
                    />
                  </DemoContainer>
                </LocalizationProvider>
                {!!error?.message?.length && (
                  <FormHelperText>{t(error?.message)}</FormHelperText>
                )}
              </>
            )}
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant={'contained'}
          color={'secondary'}
          sx={{ marginTop: '20px' }}
          type={'button'}
          onClick={() => navigate(-1)}
        >
          {t('button.cancel')}
        </Button>
        <Button
          variant={'contained'}
          sx={{ margin: '20px 0 0 10px' }}
          type={'submit'}
        >
          {t(isUpdate ? 'button.update' : 'button.create')}
        </Button>
      </Box>
    </Card>
  );
};

export default HolidayForm;
